import { supabase } from '@/integrations/supabase/client';
import { ReferentStateMachine } from './stateMachines/ReferentStateMachine';
import { AuditService } from './AuditService';
import { ValidationService } from './ValidationService';
import { ErrorHandler } from './ErrorHandler';
import { CacheService } from './index';
import { ReferentStatus } from '@/types/states';
import type { Referent } from '@/types/unified';
import type { SubmitReferentsFormData } from '@/types/referents';
import { logger } from '@/utils/productionLogger';
import {
    StateTransitionError,
    BusinessRuleViolationError,
    ValidationError
} from '@/errors/BusinessErrors';
import { validateContextAndExecute } from '@/lib/contextAwareService';
import { backgroundOperations } from '@/services/backgroundOperations';

export interface ReferentUpdateData {
    status?: ReferentStatus;
    email?: string;
    name?: string;
    position?: string;
    relation_type?: string;
}

export interface EvaluationData {
    relationTypeConfirmed: string;
    collaborationStartDate: string;
    collaborationEndDate?: string;
    ratings: Record<string, number>;
    criticalQuestions?: {
        wouldRecommend: boolean | null;
        wouldRehire: boolean | null;
        hasIncidents: boolean | null;
        incidentsDescription?: string;
    };
    audioFileUrl?: string;
    audioTranscription?: string;
    cleanedTranscription?: string;
    transcriptionSentiment?: string;
    transcriptionSentimentReason?: string;
    transcriptionKeyPoints?: string[];
    signedDocumentUrl?: string;
}

export class ReferentService {
    private auditService: AuditService;
    private validationService: ValidationService;

    constructor() {
        this.auditService = new AuditService();
        this.validationService = new ValidationService();
    }

    /**
     * Récupère un référent par son ID avec validation des permissions et cache
     */
    async getReferent(referentId: string): Promise<Referent> {
        // PHASE 2: Utiliser le nouveau CacheService
        const cacheKey = CacheService.generateKey('referent', referentId);
        const cached = CacheService.get<Referent>(cacheKey);
        if (cached) return cached;

        const result = await this.fetchReferentFromDB(referentId);
        CacheService.set(cacheKey, result);
        return result;
    }

    /**
     * Récupère un référent directement depuis la base de données
     */
    private async fetchReferentFromDB(referentId: string): Promise<Referent> {
        const { data, error } = await (supabase as any)
            .from('referents')
            .select('*')
            .eq('id', referentId)
            .single();

        if (error) {
            throw ErrorHandler.handleDatabaseError(error);
        }

        if (!data) {
            throw new ValidationError('Référent introuvable');
        }

        return data as unknown as Referent;
    }

    /**
     * Met à jour le statut d'un référent avec validation des transitions
     */
    async updateReferentStatus(
        referentId: string,
        newStatus: ReferentStatus,
        reason?: string,
        context?: Record<string, unknown>
    ): Promise<Referent> {
        return await validateContextAndExecute(async () => {
            // 1. Récupération de l'état actuel
            const referent = await this.getReferent(referentId);
            const oldStatus = referent.status as ReferentStatus;

            // 2. Validation de la transition
            if (!ReferentStateMachine.canTransition(oldStatus, newStatus)) {
                throw new StateTransitionError(
                    oldStatus,
                    newStatus,
                    'referent',
                    { referentId }
                );
            }

            // 3. Validation des règles métier
            await this.validateBusinessRules(referent, newStatus, context || {});

            // 4. Mise à jour avec audit
            const { data, error } = await (supabase as any)
                .from('referents')
                .update({
                    status: newStatus,
                    updated_at: new Date().toISOString()
                })
                .eq('id', referentId)
                .select()
                .single();

            if (error) {
                throw ErrorHandler.handleDatabaseError(error);
            }

            // 5. Invalidation du cache pour ce référent et sa campagne
            CacheService.invalidateReferent(referentId);

            // 6. Opérations non-critiques en arrière-plan (ne doivent pas faire échouer la transaction)
            try {
                // Audit en arrière-plan avec retry automatique
                backgroundOperations.queueAuditOperation(
                    'referent',
                    referentId,
                    oldStatus,
                    newStatus,
                    context?.userId as string,
                    reason
                );

                // Événement en arrière-plan avec retry automatique
                backgroundOperations.queueEventOperation(
                    referentId,
                    referent.campaign_id,
                    oldStatus,
                    newStatus
                );

                // Traitement immédiat pour les changements critiques (passage à Complété)
                if (newStatus === ReferentStatus.COMPLETE) {
                    try {
                        const { CampaignService } = await import('./campaignService');
                        const campaignService = new CampaignService();
                        await campaignService.autoProgressCampaignDirect(referent.campaign_id);
                        logger.info('[ReferentService] Campaign progression triggered immediately', {
                            campaignId: referent.campaign_id,
                            referentId
                        });
                    } catch (progressError) {
                        logger.warn('[ReferentService] Immediate campaign progression failed, will retry via events', {
                            error: progressError as Error,
                            campaignId: referent.campaign_id
                        });
                    }
                }

                logger.info('[ReferentService] Background operations queued successfully', {
                    referentId,
                    oldStatus,
                    newStatus
                });
            } catch (backgroundError) {
                // Les opérations en arrière-plan ne doivent jamais faire échouer la transaction principale
                logger.warn('[ReferentService] Failed to queue background operations, continuing normally', {
                    error: backgroundError as Error,
                    referentId,
                    oldStatus,
                    newStatus
                });
            }

            return data as unknown as Referent;
        }, 'referent');
    }

    /**
     * Marque un référent comme ayant accédé au formulaire
     */
    async markReferentAccessed(referentId: string): Promise<boolean> {
        try {
            const referent = await this.getReferent(referentId);

            // Seuls les référents "Invité" ou "Relancé" peuvent passer à "A répondu"
            if (referent.status === ReferentStatus.INVITE || referent.status === ReferentStatus.RELANCE) {
                await this.updateReferentStatus(
                    referentId,
                    ReferentStatus.A_REPONDU,
                    'Accès au formulaire d\'évaluation'
                );
                return true;
            }

            return false;
        } catch (accessError) {
            logger.error('Erreur lors du marquage d\'accès référent', accessError as Error);
            throw accessError;
        }
    }

    /**
     * Soumet les référents pour une campagne
     */
    async submitReferents(
        campaignToken: string,
        data: SubmitReferentsFormData & { fingerprintConsent?: boolean; deviceFingerprint?: string | null }
    ): Promise<{ emailSuccess: boolean; referentIds?: string[]; submissionId?: string }> {
        try {
            // 1. Validation des données
            await this.validationService.validate('submitReferents', data);

            // 1.5. Validation supplémentaire côté frontend
            if (!campaignToken || campaignToken.length < 10) {
                throw new ValidationError('Token de campagne invalide');
            }

            if (!data.referents || data.referents.length === 0) {
                throw new ValidationError('Aucun référent à soumettre');
            }

            // Valider chaque référent
            data.referents.forEach((referent, index) => {
                if (!referent.name || !referent.email) {
                    throw new ValidationError(`Référent ${index + 1}: nom et email requis`);
                }
                if (!referent.email.includes('@')) {
                    throw new ValidationError(`Référent ${index + 1}: format d'email invalide`);
                }
            });

            // 2. Appel de la Edge Function pour soumettre les référents avec fingerprinting
            logger.debug('Appel Edge Function submit-candidate-referents', {
                campaignToken: campaignToken.substring(0, 8) + '...',
                referentsCount: data.referents.length,
                hasFingerprint: !!data.deviceFingerprint
            });

            const { data: result, error } = await supabase.functions.invoke('submit-candidate-referents', {
                body: {
                    campaign_token: campaignToken,
                    referents: data.referents,
                    fingerprintConsent: data.fingerprintConsent || false,
                    deviceFingerprint: data.deviceFingerprint || null
                }
            });

            if (error) {
                logger.error('Erreur Edge Function submit-candidate-referents', error as Error, {
                    status: (error as any).status,
                    message: (error as any).message,
                    campaignToken: campaignToken.substring(0, 8) + '...'
                });
                throw ErrorHandler.handleEdgeFunctionError(error);
            }

            if (result.already_submitted) {
                return { 
                    emailSuccess: true,
                    referentIds: result.referent_ids || [],
                    submissionId: result.submission_id
                };
            }

            // 3. Audit de la soumission (utiliser campaign_id si disponible, sinon désactiver l'audit)
            if (result.campaign_id) {
                await this.auditService.logBusinessAction(
                    'referent_submission',
                    result.campaign_id,
                    {
                        referent_count: data.referents.length,
                        email_success: result.email_success,
                        campaign_token: campaignToken // Garder le token dans les détails pour référence
                    }
                );
            } else {
                // Log sans audit si pas de campaign_id disponible
                logger.debug('Audit désactivé: campaign_id non disponible', { campaignToken });
            }

            return { 
                emailSuccess: result.email_success || false,
                referentIds: result.referent_ids || [],
                submissionId: result.submission_id
            };
        } catch (submitError) {
            logger.error('Erreur dans submitReferents', submitError as Error);
            throw submitError;
        }
    }

    /**
     * Soumet une évaluation complète de référent
     */
    async submitEvaluation(
        referentId: string,
        campaignId: string,
        evaluationData: EvaluationData,
        options: { allowUpdate?: boolean } = {}
    ): Promise<void> {
        return await validateContextAndExecute(async () => {
            // 1. Validation des données d'évaluation
            await this.validationService.validate('referentEvaluation', evaluationData);

            // 2. Récupération du référent pour validation
            const referent = await this.getReferent(referentId);

            // 🛡️ PROTECTION CRITIQUE : Vérifier que l'évaluation n'est pas déjà finalisée
            if (referent.status === ReferentStatus.COMPLETE) {
                throw new BusinessRuleViolationError(
                    'Cette évaluation a déjà été finalisée et ne peut plus être modifiée',
                    'referent',
                    { referentId, currentStatus: referent.status }
                );
            }

            // 3. Vérification que le référent peut soumettre une évaluation
            if (referent.status !== ReferentStatus.A_REPONDU) {
                throw new BusinessRuleViolationError(
                    'Le référent doit avoir accédé au formulaire avant de pouvoir soumettre une évaluation',
                    'referent',
                    { referentId, currentStatus: referent.status }
                );
            }

            // 4. Préparation des données pour insertion
            const insertData = {
                referent_id: referentId,
                campaign_id: campaignId,
                relation_type_confirmed: evaluationData.relationTypeConfirmed,
                collaboration_start_date: evaluationData.collaborationStartDate,
                collaboration_end_date: evaluationData.collaborationEndDate,
                // Scores standards pour compatibilité
                performance_score: evaluationData.ratings.performance || 5,
                adaptability_score: evaluationData.ratings.adaptability || 5,
                learning_score: evaluationData.ratings.learning || 5,
                reliability_score: evaluationData.ratings.reliability || 5,
                communication_score: evaluationData.ratings.communication || 5,
                // Nouveaux scores personnalisés
                custom_criteria_scores: evaluationData.ratings,
                // Questions critiques
                would_recommend: evaluationData.criticalQuestions?.wouldRecommend,
                would_rehire: evaluationData.criticalQuestions?.wouldRehire,
                has_incidents: evaluationData.criticalQuestions?.hasIncidents,
                incidents_description: evaluationData.criticalQuestions?.incidentsDescription,
                // Fichiers
                audio_file_url: evaluationData.audioFileUrl,
                audio_transcription: evaluationData.audioTranscription,
                cleaned_transcription: evaluationData.cleanedTranscription,
                transcription_sentiment: evaluationData.transcriptionSentiment,
                transcription_sentiment_reason: evaluationData.transcriptionSentimentReason,
                transcription_key_points: evaluationData.transcriptionKeyPoints,
                signed_document_url: evaluationData.signedDocumentUrl,
                // Pas d'IP ou user agent pour la confidentialité
                ip_address: null,
                user_agent: null,
            };

            // 5. Insertion intelligente de la réponse (INSERT ou UPSERT selon les options)
            const { error } = options.allowUpdate
                ? await (supabase as any)
                    .from('referent_responses')
                    .upsert(insertData, { onConflict: 'referent_id,campaign_id' })
                : await (supabase as any)
                    .from('referent_responses')
                    .insert(insertData);

            if (error) {
                throw ErrorHandler.handleDatabaseError(error);
            }

            // 6. Mise à jour du statut du référent
            await this.updateReferentStatus(
                referentId,
                ReferentStatus.COMPLETE,
                'Évaluation soumise avec succès'
            );

            // 7. Audit de la soumission
            await this.auditService.logBusinessAction(
                'evaluation_submitted',
                referentId,
                {
                    campaign_id: campaignId,
                    has_audio: !!evaluationData.audioFileUrl,
                    has_document: !!evaluationData.signedDocumentUrl,
                    criteria_count: Object.keys(evaluationData.ratings).length
                }
            );

        }, 'referent');
    }

    /**
     * Vérifie si une évaluation est complète
     */
    async checkEvaluationComplete(referentId: string): Promise<boolean> {
        const { data, error } = await (supabase as any)
            .from('referent_responses')
            .select('id')
            .eq('referent_id', referentId)
            .single();

        return !error && !!data;
    }

    /**
     * Récupère tous les référents d'une campagne avec cache
     */
    async getCampaignReferents(campaignId: string, organizationId?: string): Promise<Referent[]> {
        if (organizationId) {
            // PHASE 2: Utiliser le nouveau CacheService
            const cacheKey = CacheService.generateKey('campaign_referents', campaignId);
            const cached = CacheService.get<Referent[]>(cacheKey);
            if (cached) return cached;

            // OPTIMISÉ: Méthode directe avec cache intelligent
            const result = await this.fetchCampaignReferentsFromDB(campaignId);
            CacheService.set(cacheKey, result, 5 * 60 * 1000); // 5min TTL pour les référents
            return result;
        }

        return await this.fetchCampaignReferentsFromDB(campaignId);
    }

    /**
     * Récupère les référents d'une campagne directement depuis la base de données
     */
    private async fetchCampaignReferentsFromDB(campaignId: string): Promise<Referent[]> {
        const { data, error } = await (supabase as any)
            .from('referents')
            .select('*')
            .eq('campaign_id', campaignId)
            .order('created_at', { ascending: true });

        if (error) {
            throw ErrorHandler.handleDatabaseError(error);
        }

        return data as unknown as Referent[];
    }

    /**
     * Vérifie si tous les référents d'une campagne ont terminé
     */
    async checkAllReferentsCompleted(campaignId: string): Promise<boolean> {
        const referents = await this.getCampaignReferents(campaignId);

        if (referents.length === 0) {
            return false;
        }

        return referents.every(referent =>
            referent.status === ReferentStatus.COMPLETE
        );
    }

    /**
     * Valide les règles métier pour les transitions d'état
     */
    private async validateBusinessRules(
        referent: Referent,
        newStatus: ReferentStatus,
        _context?: Record<string, unknown>
    ): Promise<void> {
        // Règle : Un référent ne peut pas passer à "Complété" sans avoir soumis d'évaluation
        if (newStatus === ReferentStatus.COMPLETE) {
            const hasEvaluation = await this.checkEvaluationComplete(referent.id);
            if (!hasEvaluation) {
                throw new BusinessRuleViolationError(
                    'Le référent ne peut pas être marqué comme complété sans avoir soumis d\'évaluation',
                    'referent',
                    { referentId: referent.id }
                );
            }
        }

        // Règle : Un référent ne peut pas être relancé s'il a déjà terminé
        if (newStatus === ReferentStatus.RELANCE && referent.status === ReferentStatus.COMPLETE) {
            throw new BusinessRuleViolationError(
                'Un référent qui a terminé son évaluation ne peut pas être relancé',
                'referent',
                { referentId: referent.id, currentStatus: referent.status }
            );
        }
    }


}

// Export d'une instance par défaut pour compatibilité
export const referentService = new ReferentService();

// Export des fonctions legacy pour compatibilité
export const submitReferents = (campaignToken: string, data: SubmitReferentsFormData) =>
    referentService.submitReferents(campaignToken, data);

export const markReferentAccessed = (referentId: string) =>
    referentService.markReferentAccessed(referentId);

// SUPPRIMÉ : submitReferentResponse legacy
// Cette fonction était dupliquée et non utilisée en production.
// Le formulaire web utilise maintenant l'adaptateur dans referentFormSubmission.ts
// qui délègue vers submitEvaluation() avec les bonnes options.